import html2canvas from 'html2canvas'
import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { sanitizeOklch } from '../../../utils/sanitizeOklch'
import { BillTemplate } from '../export-bill/components/BillTemplate'
import type { BillData } from '../export-bill/interfaces/exportBill'
import {
  fetchBillByRoomAndMonth,
  fetchSnapshotBillByRoomAndMonth,
} from '../export-bill/services/exportBillService'

// A4 at 96 DPI
const A4_W = 794
const A4_H = 1123

const html2canvasOpts = {
  width: A4_W,
  height: A4_H,
  scale: 2,
  useCORS: true,
  allowTaint: false,
  logging: false,
  onclone: (clonedDoc: Document) => sanitizeOklch(clonedDoc),
}

export default function BillViewPage() {
  const { roomNumber } = useParams<{ roomNumber: string }>()
  const [searchParams] = useSearchParams()
  const month = searchParams.get('month')

  const [bill, setBill] = useState<BillData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRendering, setIsRendering] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const billRef = useRef<HTMLDivElement | null>(null)

  // Fetch bill data
  useEffect(() => {
    if (!roomNumber || !month) {
      setError('Room number and month are required.')
      setIsLoading(false)
      return
    }

    const load = async () => {
      setIsLoading(true)
      setImageUrl(null)
      setError(null)
      try {
        const snapshotBill = await fetchSnapshotBillByRoomAndMonth(
          roomNumber,
          month,
        )
        if (snapshotBill) {
          setBill(snapshotBill)
        } else {
          const liveBill = await fetchBillByRoomAndMonth(roomNumber, month)
          setBill(liveBill)
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to fetch bill.')
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [roomNumber, month])

  // Render bill template to image after it mounts
  useEffect(() => {
    if (!bill || !billRef.current) return

    setIsRendering(true)
    html2canvas(billRef.current, html2canvasOpts)
      .then((canvas) => setImageUrl(canvas.toDataURL('image/png')))
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Failed to render bill.'),
      )
      .finally(() => setIsRendering(false))
  }, [bill])

  const busy = isLoading || isRendering

  if (busy && !bill) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-300">
        <span className="text-gray-600">Loading bill...</span>
      </div>
    )
  }

  if (error || (!bill && !busy)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-300">
        <span className="text-red-500">{error ?? 'Bill not found.'}</span>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen justify-center bg-gray-300 py-4">
      {/* Off-screen template captured by html2canvas; hidden once image is ready */}
      {bill && !imageUrl && (
        <div
          ref={billRef}
          className="-left-[9999px] pointer-events-none fixed top-0"
        >
          <BillTemplate {...bill} />
        </div>
      )}

      {isRendering && (
        <div className="flex items-center self-center">
          <span className="text-gray-600">Rendering...</span>
        </div>
      )}

      {imageUrl && (
        <img
          src={imageUrl}
          alt={`Bill room ${roomNumber} ${month}`}
          className="h-auto w-[397px] shadow-2xl"
        />
      )}
    </div>
  )
}
